import React, { useEffect, useState } from "react";
import { Modal, Nav, NavItem, NavLink } from "reactstrap";
import classnames from "classnames";
import moment from "moment";

// ─── Helpers ─────────────────────────────────────────────────────────────────

function joinPublicUrl(pathLike) {
  if (pathLike == null || pathLike === "") return "";
  const p = String(pathLike).trim();
  if (/^https?:\/\//i.test(p) || p.startsWith("data:") || p.startsWith("blob:")) return p;
  const base =
    (process.env.REACT_APP_STORAGE_TYPE === "azure"
      ? process.env.REACT_APP_STORAGE_URL
      : process.env.REACT_APP_API_PUBLIC_URL) || "";
  const rel = p.replace(/^\/+/, "");
  if (!base) return rel ? `/${rel}` : "";
  return `${base.replace(/\/+$/, "")}/${rel}`;
}

function formatDate(d, fmt = "DD-MMM-YYYY") {
  if (!d) return "—";
  const m = moment(d);
  return m.isValid() ? m.format(fmt) : "—";
}

function val(v) {
  return v != null && String(v).trim() !== "" ? v : null;
}

const STATUS_THEME = {
  TRACING:    { background: "#f1f5f9", color: "#475569", border: "1px solid #cbd5e1" },
  LOCATED:    { background: "#e0f2fe", color: "#0369a1", border: "1px solid #7dd3fc" },
  PROCESSING: { background: "#eceef0", color: "#343a40", border: "1px solid #adb5bd" },
  RECEIVED:   { background: "#fef9c3", color: "#854d0e", border: "1px solid #fde047" },
  DELIVERED: { background: "#dcfce7", color: "#166534", border: "1px solid #86efac" },
  FORWARDED: { background: "#ede9fe", color: "#5b21b6", border: "1px solid #c4b5fd" },
};

const TYPE_THEME = {
  AHL:       { background: "#dbeafe", color: "#1d4ed8", border: "1px solid #93c5fd" },
  DPR:       { background: "#fee2e2", color: "#b91c1c", border: "1px solid #fca5a5" },
  OHD:       { background: "#fef3c7", color: "#92400e", border: "1px solid #fcd34d" },
  UNCLAIMED: { background: "#f1f5f9", color: "#475569", border: "1px solid #cbd5e1" },
  CLAIMS:    { background: "#1e293b", color: "#f8fafc", border: "1px solid #334155" },
};

const pillBase = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: 26,
  padding: "0 11px",
  borderRadius: 999,
  fontSize: "0.6875rem",
  fontWeight: 600,
  letterSpacing: "0.02em",
  lineHeight: 1,
  whiteSpace: "nowrap",
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = {
  headerTitle: { fontSize: "1.1rem", fontWeight: 600, margin: 0, color: "#1e293b" },
  summaryRow: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: "8px 10px",
    padding: "12px 0 14px",
    borderBottom: "1px solid #e8ecf1",
    marginBottom: 16,
  },
  tagNumber: {
    fontFamily: "monospace",
    fontSize: "1.2rem",
    fontWeight: 700,
    color: "#0f172a",
    letterSpacing: "0.06em",
    marginRight: 4,
  },
  sectionTitle: {
    fontSize: "0.78rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: "#64748b",
    margin: "22px 0 10px",
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  sectionBody: {
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 10,
    padding: "14px 16px",
  },
  kvGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
    gap: "12px 24px",
  },
  kvLabel: { fontSize: "0.72rem", fontWeight: 600, color: "#94a3b8", marginBottom: 2 },
  kvValue: { fontSize: "0.9rem", color: "#0f172a", wordBreak: "break-word" },
  tabNav: { borderBottom: "1px solid #e2e8f0", marginBottom: 4 },
  tabLink: {
    borderRadius: "10px 10px 0 0",
    padding: "10px 16px",
    display: "flex",
    alignItems: "center",
    gap: 8,
    color: "#475569",
    cursor: "pointer",
  },
  tabLinkActive: {
    background: "linear-gradient(180deg, #e0f2fe 0%, #f0f9ff 100%)",
    color: "#0369a1",
    fontWeight: 600,
  },
  tabIcon: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.85rem",
    border: "2px solid #7dd3fc",
    background: "#fff",
    color: "#0369a1",
  },
  tabIconActive: { background: "#0369a1", color: "#fff", borderColor: "#0369a1" },
};

// ─── Sub-components ───────────────────────────────────────────────────────────

function KV({ label, children }) {
  return (
    <div>
      <div style={styles.kvLabel}>{label}</div>
      <div style={styles.kvValue}>{children ?? "—"}</div>
    </div>
  );
}

function Section({ icon, title, children }) {
  return (
    <div>
      <div style={styles.sectionTitle}>
        {icon && <i className={icon} style={{ fontSize: "1rem", color: "#0ea5e9" }} />}
        {title}
      </div>
      <div style={styles.sectionBody}>{children}</div>
    </div>
  );
}

function Pill({ theme, children }) {
  return <span style={{ ...pillBase, ...theme }}>{children}</span>;
}

// ─── Main component ───────────────────────────────────────────────────────────

export function BagViewModal({ isOpen, toggle, bag }) {
  const [activeTab, setActiveTab] = useState("bag");

  useEffect(() => {
    if (isOpen) setActiveTab("bag");
  }, [isOpen, bag?.id]);

  if (!bag) return null;

  const cf           = bag.case_file ?? {};
  const statusName   = bag.bag_status?.name ?? "";
  const caseType     = cf.type ?? "";
  const isForwarded  = statusName === "FORWARDED";
  const isDelivered  = statusName === "DELIVERED";
  const isReceived   = ["RECEIVED", "DELIVERED", "FORWARDED"].includes(statusName);
  const isTracing    = statusName === "TRACING";
  const showArriving = !isTracing && !["DPR", "OHD", "UNCLAIMED"].includes(caseType);
  const signatureSrc = val(bag.delivery_signature_data) || (val(bag.delivery_signature) ? joinPublicUrl(bag.delivery_signature) : null);
  const notes        = Array.isArray(cf.notes) ? cf.notes : [];

  const Tab = ({ id, icon, label, count }) => (
    <NavItem>
      <NavLink
        href="#"
        tag="a"
        className={classnames({ active: activeTab === id })}
        onClick={(e) => { e.preventDefault(); setActiveTab(id); }}
        style={{ ...styles.tabLink, ...(activeTab === id ? styles.tabLinkActive : {}) }}
      >
        <span style={{ ...styles.tabIcon, ...(activeTab === id ? styles.tabIconActive : {}) }}>
          <i className={`mdi ${icon}`} />
        </span>
        {label}{count != null ? ` (${count})` : ""}
      </NavLink>
    </NavItem>
  );

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" backdrop="static" scrollable id="bag-view-modal">
      <div className="modal-header" style={{ borderBottom: "none", paddingBottom: 8 }}>
        <h5 style={styles.headerTitle}>Bag details</h5>
        <button type="button" className="close" onClick={toggle} aria-label="Close">
          <span aria-hidden="true">&times;</span>
        </button>
      </div>

      <div className="modal-body pt-0">

        {/* ── Tabs ─────────────────────────────────────────────────────── */}
        <div style={styles.tabNav}>
          <Nav tabs className="nav-tabs-custom">
            <Tab id="bag"      icon="mdi-bag-suitcase"      label="Bag" />
            <Tab id="casefile" icon="mdi-file-document"     label="Case File" />
            <Tab id="notes"    icon="mdi-history"           label="Notes" count={notes.length} />
          </Nav>
        </div>

        {/* ── Summary row ──────────────────────────────────────────────── */}
        <div style={styles.summaryRow}>
          <span style={styles.tagNumber}>{bag.tag_number}</span>
          {statusName && <Pill theme={STATUS_THEME[statusName] || STATUS_THEME.TRACING}>{statusName}</Pill>}
          {caseType && <Pill theme={TYPE_THEME[caseType] || TYPE_THEME.UNCLAIMED}>{caseType}</Pill>}
          {val(cf.primary_reference) && (
            <span style={{ fontSize: "0.8rem", color: "#64748b", fontWeight: 500 }}>
              {cf.primary_reference}
            </span>
          )}
          {cf.closed && (
            <Pill theme={{ background: "#fef2f2", color: "#b91c1c", border: "1px solid #fecaca" }}>
              Case Closed
            </Pill>
          )}
        </div>

        {/* ── Bag tab ──────────────────────────────────────────────────── */}
        {activeTab === "bag" && (
          <div>
            <Section icon="mdi mdi-bag-suitcase" title="Bag info">
              <div style={styles.kvGrid}>
                <KV label="Tag Number">{val(bag.tag_number)}</KV>
                <KV label="Color Type">{val(bag.color_type)}</KV>
                <KV label="Status">{statusName || "—"}</KV>
                {showArriving && (
                  <KV label="Arriving Flight">{val(bag.location_history)}</KV>
                )}
                {val(bag.rush_tag_number) && (
                  <KV label="Rush Tag Number">{bag.rush_tag_number}</KV>
                )}
              </div>
            </Section>

            {isReceived && (
              <Section icon="mdi mdi-calendar-check" title="Received">
                <div style={styles.kvGrid}>
                  <KV label="Receive Date">{formatDate(bag.receive_date)}</KV>
                  {val(bag.received_flight) && !["UNCLAIMED", "OHD"].includes(caseType) && (
                    <KV label="Received Flight">{bag.received_flight}</KV>
                  )}
                  {val(bag.received_weight) && (
                    <KV label="Weight">{bag.received_weight} kg</KV>
                  )}
                  {caseType === "DPR" && val(bag.damage_bag_receive_date) && (
                    <KV label="DPR Receive Date">{formatDate(bag.damage_bag_receive_date)}</KV>
                  )}
                  {caseType === "DPR" && val(bag.dpr_claim) && (
                    <KV label="DPR Claim">{bag.dpr_claim}</KV>
                  )}
                </div>
              </Section>
            )}

            {isForwarded && (
              <Section icon="mdi mdi-airplane-takeoff" title="Forwarded">
                <div style={styles.kvGrid}>
                  <KV label="Forward Date">{formatDate(bag.forward_date)}</KV>
                  <KV label="Forward Flight">{val(bag.forward_flight)}</KV>
                  <KV label="Forward Type">{val(bag.forward_type?.name)}</KV>
                  <KV label="Rush Tag Number">{val(bag.rush_tag_number)}</KV>
                </div>
              </Section>
            )}

            {isDelivered && (
              <Section icon="mdi mdi-hand-okay" title="Delivered">
                <div style={styles.kvGrid}>
                  <KV label="Deliver Date">
                    {bag.deliver_date ? moment(bag.deliver_date).format("HH:mm | DD-MMM-YYYY") : "—"}
                  </KV>
                  <KV label="Recipient">{val(bag.delivery_name)}</KV>
                  <KV label="Contact">{val(bag.delivery_contact)}</KV>
                  <KV label="Identity">{val(bag.delivery_identity)}</KV>
                  <KV label="Condition">{val(bag.delivery_condition)}</KV>
                </div>
                {signatureSrc && (
                  <div style={{ marginTop: 14 }}>
                    <div style={styles.kvLabel}>Signature</div>
                    <img
                      src={signatureSrc}
                      alt="Delivery signature"
                      style={{ maxWidth: 300, height: 'auto', borderRadius: 6, border: "1px solid #e2e8f0", marginTop: 4 }}
                    />
                  </div>
                )}
              </Section>
            )}
          </div>
        )}

        {/* ── Case File tab ─────────────────────────────────────────────── */}
        {activeTab === "casefile" && (
          <div>
            <Section icon="mdi mdi-airplane" title="Flight & reference">
              <div style={styles.kvGrid}>
                <KV label="Reference">{val(cf.reference)}</KV>
                <KV label="File Reference">{val(cf.file_reference)}</KV>
                <KV label="Flight">{val(cf.flight)}</KV>
                <KV label="Flight Date">{formatDate(cf.flight_date)}</KV>
                {caseType === "DPR" && val(cf.damage_bag_claim?.name) && (
                  <KV label="Damage Bag Claim">{cf.damage_bag_claim.name}</KV>
                )}
              </div>
            </Section>

            <Section icon="mdi mdi-account" title="Passenger & location">
              <div style={styles.kvGrid}>
                <KV label="First Name">{val(cf.first_name)}</KV>
                <KV label="Last Name">{val(cf.last_name)}</KV>
                <KV label="Address">{val(cf.address?.name)}</KV>
                {val(cf.departure_date) && (
                  <KV label="Passenger Departure">{formatDate(cf.departure_date)}</KV>
                )}
                {caseType === "CLAIMS" && (
                  <>
                    {val(cf.claim_type?.name) && <KV label="Claim Type">{cf.claim_type.name}</KV>}
                    {val(cf.claimed_item) && <KV label="Claimed Item">{cf.claimed_item}</KV>}
                  </>
                )}
              </div>
            </Section>

            {val(cf.remarks) && (
              <div>
                <div style={styles.sectionTitle}>
                  <i className="mdi mdi-comment-text-outline" style={{ fontSize: "1rem", color: "#0ea5e9" }} />
                  Remarks
                </div>
                <div style={{
                  background: "#fff",
                  border: "1px solid #e2e8f0",
                  borderRadius: 8,
                  padding: "12px 14px",
                  minHeight: 48,
                  fontSize: "0.9rem",
                  color: "#334155",
                  whiteSpace: "pre-wrap",
                }}>
                  {cf.remarks}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Notes tab ─────────────────────────────────────────────────── */}
        {activeTab === "notes" && (
          <div>
            {notes.length === 0 ? (
              <p className="text-muted mb-0 mt-2">No notes on this case.</p>
            ) : (
              notes.map((note, idx) => (
                <div
                  key={note.id ?? idx}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "auto 1fr",
                    gap: "12px 16px",
                    padding: "14px 16px",
                    marginBottom: 10,
                    background: "#f8fafc",
                    borderRadius: 10,
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <div style={{ fontWeight: 700, color: "#94a3b8", fontSize: "0.85rem", minWidth: 24 }}>
                    {notes.length - idx}
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.8rem" }}>
                      {note.created_at ? moment(note.created_at).format("Do MMM YYYY | HH:mm") : "—"}
                    </div>
                    <div style={{ fontSize: "0.75rem", color: "#64748b", marginTop: 4 }}>
                      {note.user?.name ?? note.user?.rcno ?? "—"}
                    </div>
                    <div style={{ marginTop: 10, fontSize: "0.9rem", color: "#334155", lineHeight: 1.5 }}>
                      {note.note ?? note.body ?? "—"}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

      </div>

      <div className="modal-footer">
        <button type="button" className="btn btn-secondary waves-effect" onClick={toggle}>
          Close
        </button>
      </div>
    </Modal>
  );
}
